/**
 * Template de prise de besoin : Plan d'incident vie privée / confidentialité v1
 * Aligné sur Loi 25, RGPD, ISO 27001 et ITIL 4
 * Plan spécifique pour la gestion des incidents de protection des renseignements personnels
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePlanIncidentViePriveeV1: IntakeTemplate = {
  id: 'plan-incident-vie-privee-v1',
  name: 'Prise de besoin Plan d\'incident vie privée / confidentialité v1',
  description:
    'Questionnaire complet pour élaborer un plan d\'incident vie privée conforme Loi 25, RGPD, ISO 27001 et ITIL 4. Définit les procédures de détection, évaluation, notification CAI, avis aux personnes concernées et mesures correctives.',
  documentTypeCode: 'PRIVACY_INCIDENT_PLAN',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Types de Renseignements',
      description: 'Informations sur l\'entreprise et types de renseignements personnels',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour le plan d\'incident vie privée.',
        },
        {
          id: 'types_renseignements',
          label: 'Quels types de renseignements personnels votre organisation détient-elle ?',
          type: 'multiselect',
          options: [
            'Renseignements d\'identité',
            'Renseignements financiers',
            'Renseignements médicaux',
            'Renseignements RH',
            'Renseignements de géolocalisation',
            'Renseignements biométriques',
            'Renseignements judiciaires',
            'Autre',
          ],
          required: true,
          helpText: 'Loi 25: Identification des types de renseignements pour évaluation des risques.',
        },
        {
          id: 'volume_renseignements',
          label: 'Quel est approximativement le volume de renseignements personnels détenus ?',
          type: 'select',
          options: [
            'Moins de 100 personnes',
            '100-1000 personnes',
            '1000-10000 personnes',
            'Plus de 10000 personnes',
            'À déterminer',
          ],
          helpText: 'Loi 25: Volume pour évaluation de l\'impact potentiel.',
        },
        {
          id: 'ton_document_plan',
          label: 'Quel ton souhaitez-vous pour ce plan ?',
          type: 'select',
          options: [
            'Formel et juridique (ton impératif, obligations légales)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité d\'utilisation du plan.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Détection & Évaluation',
      description: 'Procédures de détection et d\'évaluation des incidents',
      icon: 'Eye',
      questions: [
        {
          id: 'sources_detection',
          label: 'Quelles sont les sources de détection d\'incidents vie privée ?',
          type: 'multiselect',
          options: [
            'Monitoring des accès',
            'Alertes de sécurité',
            'Rapports utilisateurs',
            'Audits de conformité',
            'Notifications de violations',
            'Détection d\'anomalies',
            'Autre',
          ],
          required: true,
          helpText: 'Loi 25: Sources de détection pour identification rapide.',
        },
        {
          id: 'processus_evaluation',
          label: 'Quel est le processus d\'évaluation d\'un incident vie privée ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Détection → Évaluation impact → Évaluation risque → Classification sévérité...',
          helpText: 'Loi 25: Processus structuré pour évaluation efficace.',
        },
        {
          id: 'criteres_evaluation',
          label: 'Quels critères sont utilisés pour évaluer la sévérité ?',
          type: 'multiselect',
          options: [
            'Nombre de personnes concernées',
            'Type de renseignements',
            'Sensibilité des renseignements',
            'Probabilité d\'utilisation préjudiciable',
            'Préjudice réel ou potentiel',
            'Conformité réglementaire',
            'Autre',
          ],
          helpText: 'Loi 25: Critères pour évaluation de la sévérité.',
        },
        {
          id: 'dpo_responsable',
          label: 'Qui est le DPO (Délégué à la protection des données) ou responsable ?',
          type: 'text',
          placeholder: 'Ex: DPO interne, Responsable conformité, Consultant externe...',
          helpText: 'Loi 25: DPO pour gestion des incidents vie privée.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Notification CAI & Avis Personnes',
      description: 'Procédures de notification CAI et avis aux personnes concernées',
      icon: 'FileCheck',
      questions: [
        {
          id: 'critere_notification_cai',
          label: 'Quel est le critère pour notifier la CAI (Commission d\'accès à l\'information) ?',
          type: 'select',
          options: [
            'Tous les incidents',
            'Incidents avec préjudice réel',
            'Incidents avec risque élevé',
            'Selon évaluation DPO',
            'Autre',
          ],
          required: true,
          helpText: 'Loi 25: Critère pour notification CAI (obligation légale).',
        },
        {
          id: 'delai_notification_cai',
          label: 'Quel est le délai pour notifier la CAI ?',
          type: 'select',
          options: [
            'Immédiatement (dans les heures)',
            'Dans les 24 heures',
            'Dans les 72 heures',
            'Selon évaluation',
            'Autre',
          ],
          helpText: 'Loi 25: Délai pour notification CAI (généralement dans les 72 heures).',
        },
        {
          id: 'processus_notification_cai',
          label: 'Quel est le processus de notification à la CAI ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Évaluation → Préparation formulaire → Soumission CAI → Suivi...',
          helpText: 'Loi 25: Processus structuré pour notification CAI.',
        },
        {
          id: 'critere_avis_personnes',
          label: 'Quel est le critère pour aviser les personnes concernées ?',
          type: 'select',
          options: [
            'Tous les incidents',
            'Incidents avec préjudice réel',
            'Incidents avec risque élevé',
            'Selon évaluation DPO',
            'Autre',
          ],
          required: true,
          helpText: 'Loi 25: Critère pour avis aux personnes concernées.',
        },
        {
          id: 'delai_avis_personnes',
          label: 'Quel est le délai pour aviser les personnes concernées ?',
          type: 'select',
          options: [
            'Immédiatement',
            'Dans les 24 heures',
            'Dans les 72 heures',
            'Selon évaluation',
            'Autre',
          ],
          helpText: 'Loi 25: Délai pour avis aux personnes concernées.',
        },
        {
          id: 'methode_avis_personnes',
          label: 'Quelle méthode est utilisée pour aviser les personnes concernées ?',
          type: 'multiselect',
          options: [
            'Courriel',
            'Courrier postal',
            'Notification sur site web',
            'Appel téléphonique',
            'Autre',
          ],
          helpText: 'Loi 25: Méthode pour avis efficace aux personnes concernées.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Mesures Correctives & Documentation',
      description: 'Mesures correctives et documentation',
      icon: 'Shield',
      questions: [
        {
          id: 'mesures_correctives',
          label: 'Quelles mesures correctives sont prévues ?',
          type: 'multiselect',
          options: [
            'Correction de la vulnérabilité',
            'Renforcement des contrôles',
            'Formation supplémentaire',
            'Modification des processus',
            'Amélioration de la sécurité',
            'Autre',
          ],
          required: true,
          helpText: 'Loi 25: Mesures correctives pour prévenir les récurrences.',
        },
        {
          id: 'processus_correctives',
          label: 'Quel est le processus de mise en œuvre des mesures correctives ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Identification mesures → Planification → Mise en œuvre → Vérification...',
          helpText: 'Loi 25: Processus structuré pour mesures correctives.',
        },
        {
          id: 'documentation_incident',
          label: 'Quelle documentation doit être créée pour chaque incident ?',
          type: 'multiselect',
          options: [
            'Rapport d\'incident',
            'Formulaire notification CAI',
            'Avis aux personnes concernées',
            'Journal des actions prises',
            'Analyse de cause racine',
            'Plan d\'amélioration',
            'Autre',
          ],
          helpText: 'Loi 25: Documentation essentielle pour conformité et traçabilité.',
        },
        {
          id: 'conservation_documentation',
          label: 'Combien de temps conservez-vous la documentation des incidents ?',
          type: 'select',
          options: [
            '1 an',
            '3 ans',
            '5 ans',
            '7 ans',
            'Indéfiniment',
            'À déterminer',
          ],
          helpText: 'Loi 25: Conservation pour conformité réglementaire.',
        },
        {
          id: 'revision_plan',
          label: 'À quelle fréquence souhaitez-vous réviser ce plan ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Après chaque incident', 'À déterminer'],
          helpText: 'Loi 25: Révision régulière essentielle pour maintenir la conformité.',
        },
      ],
    },
  ],
};





